import { Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { MetadataList } from '../../shared/detail/MetadataList';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { reportFilterLabel } from './labels';
import type { ReportCurrencyTotals, ReportMeta } from './types';

export function ReportSummary({
  language,
  meta,
  totals,
}: {
  readonly language: TimeLanguage;
  readonly meta: ReportMeta;
  readonly totals: readonly ReportCurrencyTotals[];
}) {
  const { t } = useTranslation();
  return (
    <Stack spacing={2}>
      <Paper component="section" sx={{ p: 2 }} variant="outlined">
        <Typography component="h2" variant="h6">
          {t('reports.meta.title')}
        </Typography>
        <MetadataList
          items={[
            { label: t('reports.meta.snapshotId'), value: meta.snapshotId },
            { label: t('reports.meta.timeBasis'), value: meta.timeBasis },
            { label: t('reports.meta.timezone'), value: meta.timezone },
            {
              label: t('reports.meta.reportingAsOf'),
              value: formatTimestamp(meta.reportingAsOf, meta.timezone, language).display,
            },
            {
              label: t('reports.meta.refreshedAt'),
              value: formatTimestamp(meta.refreshedAt, meta.timezone, language).display,
            },
            {
              label: t('reports.meta.stale'),
              value: meta.stale ? t('common.yes') : t('common.no'),
            },
          ]}
        />
      </Paper>
      <Paper component="section" sx={{ p: 2 }} variant="outlined">
        <Typography component="h2" variant="h6">
          {t('reports.meta.normalizedFilters')}
        </Typography>
        <MetadataList
          items={Object.entries(meta.normalizedFilters).map(([key, value]) => ({
            label: reportFilterLabel(key, t),
            value: value === null ? t('common.notAvailable') : String(value),
          }))}
        />
      </Paper>
      <Paper component="section" sx={{ p: 2 }} variant="outlined">
        <Typography component="h2" variant="h6">
          {t('reports.totals.title')}
        </Typography>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {totals.map((total) => (
            <div key={total.currencyCode}>
              <Typography component="h3" sx={{ fontWeight: 700 }} variant="subtitle1">
                {total.currencyCode}
              </Typography>
              <MetadataList
                items={[
                  { label: t('reports.totals.playAmount'), value: total.playAmount },
                  {
                    label: t('reports.totals.agentCommissionGross'),
                    value: total.agentCommissionGross,
                  },
                  {
                    label: t('reports.totals.affiliateCommissionGross'),
                    value: total.affiliateCommissionGross,
                  },
                  {
                    label: t('reports.totals.commissionReversals'),
                    value: total.commissionReversals,
                  },
                  {
                    label: t('reports.totals.commissionAdjustments'),
                    value: total.commissionAdjustments,
                  },
                  { label: t('reports.totals.netCommission'), value: total.netCommission },
                  { label: t('reports.totals.walletAvailable'), value: total.walletAvailable },
                  { label: t('reports.totals.walletReserved'), value: total.walletReserved },
                  {
                    label: t('reports.totals.withdrawalRequested'),
                    value: total.withdrawalRequested,
                  },
                  {
                    label: t('reports.totals.withdrawalReserved'),
                    value: total.withdrawalReserved,
                  },
                  {
                    label: t('reports.totals.withdrawalCompleted'),
                    value: total.withdrawalCompleted,
                  },
                  {
                    label: t('reports.totals.withdrawalReleased'),
                    value: total.withdrawalReleased,
                  },
                  { label: t('reports.totals.withdrawalUnknown'), value: total.withdrawalUnknown },
                ]}
              />
            </div>
          ))}
        </Stack>
      </Paper>
    </Stack>
  );
}
